/*
 * Checkbox add-ons on the product page (snippets/product-upsell-checkbox.liquid).
 *
 * When the main add to cart form builds its FormData, checked add-ons are
 * appended and the request is converted to the Ajax cart "items[]" format, so
 * the main product and its add-ons land in the cart in one request.
 *
 * The add to cart button price shows the total of the selected variant plus
 * checked add-ons. The main product price stays static: it always shows the
 * selected variant's own price.
 */
if (!customElements.get('product-upsell-checkbox')) {
  customElements.define(
    'product-upsell-checkbox',
    class ProductUpsellCheckbox extends HTMLElement {
      constructor() {
        super();
        this.onFormData = this.onFormData.bind(this);
      }

      connectedCallback() {
        this.form = document.getElementById(this.dataset.productFormId);
        if (!this.form) return;

        this.form.addEventListener('formdata', this.onFormData);
        this.querySelectorAll('[data-upsell-item]').forEach((item) => this.initItem(item));

        this.baseVariant = {
          price: Number(this.dataset.basePrice) || 0,
          compare_at_price: Number(this.dataset.baseCompareAtPrice) || 0,
        };
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-upsell-checkbox]')) this.updateTotals();
        });

        if (typeof subscribe === 'function') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source !== 'product-form' || !this.pendingAdd) return;
            this.pendingAdd = false;
            this.querySelectorAll('[data-upsell-checkbox]:checked').forEach((checkbox) => {
              checkbox.checked = false;
            });
            this.updateTotals();
          });
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
            if (event.source === 'product-form') this.pendingAdd = false;
          });
          // Product info re-renders the button price on variant change;
          // re-apply the add-on total on top of the new variant.
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data?.sectionId !== this.dataset.sectionId || !event.data.variant) return;
            this.baseVariant = event.data.variant;
            this.updateTotals();
          });
        }
      }

      disconnectedCallback() {
        this.form?.removeEventListener('formdata', this.onFormData);
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        this.variantChangeUnsubscriber?.();
      }

      initItem(item) {
        const selects = [...item.querySelectorAll('[data-upsell-option]')];
        if (!selects.length) return;

        let variants = [];
        try {
          variants = JSON.parse(item.querySelector('[data-upsell-variants]')?.textContent || '[]');
        } catch (e) {
          console.error(e);
        }

        selects.forEach((select) => {
          select.addEventListener('change', () => {
            const values = selects.map((s) => s.value);
            const variant = variants.find((v) => v.options.every((value, index) => value === values[index]));
            this.updateItem(item, variant);
          });
        });
      }

      updateItem(item, variant) {
        const checkbox = item.querySelector('[data-upsell-checkbox]');
        const price = item.querySelector('[data-upsell-price]');
        const soldOut = item.querySelector('[data-upsell-sold-out]');
        const image = item.querySelector('[data-upsell-image]');
        const available = Boolean(variant && variant.available);

        if (variant) {
          checkbox.value = variant.id;
          checkbox.dataset.price = variant.price_cents;
          price.textContent = variant.price;
          if (image && variant.image) {
            image.removeAttribute('srcset');
            image.src = variant.image;
          }
        }

        checkbox.disabled = !available;
        if (!available) checkbox.checked = false;
        price.hidden = !variant;
        soldOut.hidden = available;
        this.updateTotals();
      }

      updateTotals() {
        const addOnsTotal = [...this.querySelectorAll('[data-upsell-checkbox]:checked:not(:disabled)')].reduce(
          (sum, checkbox) => sum + (Number(checkbox.dataset.price) || 0),
          0
        );
        const price = (Number(this.baseVariant.price) || 0) + addOnsTotal;
        const format = (cents) => formatMoney(cents, window.theme.settings.money_with_currency_format);

        const buttonPrice = this.form.querySelector('.buttonPrice');
        if (buttonPrice && !buttonPrice.classList.contains('hidden')) {
          buttonPrice.textContent = `— ${format(price)}`;
        }
      }

      onFormData(event) {
        const checked = [...this.querySelectorAll('[data-upsell-checkbox]:checked:not(:disabled)')];
        const formData = event.formData;
        if (!checked.length || !formData.get('id')) return;

        const isMainItemKey = (key) =>
          key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties[');
        const mainItemEntries = [...formData.entries()].filter(([key]) => isMainItemKey(key));

        new Set(mainItemEntries.map(([key]) => key)).forEach((key) => formData.delete(key));
        mainItemEntries.forEach(([key, value]) => {
          const itemKey = key.startsWith('properties[')
            ? `items[0][properties]${key.slice('properties'.length)}`
            : `items[0][${key}]`;
          formData.append(itemKey, value);
        });
        if (!formData.has('items[0][quantity]')) formData.append('items[0][quantity]', '1');

        checked.forEach((checkbox, index) => {
          formData.append(`items[${index + 1}][id]`, checkbox.value);
          formData.append(`items[${index + 1}][quantity]`, '1');
        });

        this.pendingAdd = true;
      }
    }
  );
}
